import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { getDemoClients, isDemoOrganization } from '../services/demoData'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import SearchBar from '../components/SearchBar'
import BulkActions from '../components/BulkActions'
import Pagination from '../components/Pagination'
import ClientImportExport from '../components/ClientImportExport'
import { Users, Plus, Download, Trash2, Edit, Eye, Upload, FileSpreadsheet, X, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getInitials } from '../utils/helpers'
import { exportClientsToPDF } from '../utils/pdfExport'
import { SkeletonContainer, SkeletonCard } from '../components/SkeletonLoader'
import DeleteConfirmationModal from '../components/DeleteConfirmationModal'
import { useToast } from '../contexts/ToastContext'

const ITEMS_PER_PAGE = 12

const Clients = () => {
  const { t } = useTranslation()
  const { success, error: showError } = useToast()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [showImportExport, setShowImportExport] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [clientsToDelete, setClientsToDelete] = useState([])
  const [isDeleting, setIsDeleting] = useState(false)
  const [showClientForm, setShowClientForm] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [clientFormData, setClientFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
  })
  const navigate = useNavigate()
  const organizationId = useOrganizationId()
  const { canWrite, isAdmin } = useOrganization()

  useEffect(() => {
    if (organizationId) {
      fetchClients()
    }
  }, [organizationId])

  const { isDemo, organization } = useSubscriptionStatus()

  const fetchClients = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      
      // For demo organizations, use demo data
      if (isDemo && isDemoOrganization(organization)) {
        const demoClients = getDemoClients()
        setClients(demoClients)
        setLoading(false)
        return
      }

      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setClients(data || [])
    } catch (error) {
      console.error('Error fetching clients:', error)
      alert(t('clients.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients
    const term = searchTerm.toLowerCase()
    return clients.filter(
      (client) =>
        `${client.first_name} ${client.last_name}`.toLowerCase().includes(term) ||
        client.email?.toLowerCase().includes(term) ||
        client.phone?.toLowerCase().includes(term)
    )
  }, [clients, searchTerm])

  const paginatedClients = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredClients.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredClients, currentPage])

  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE)

  const handleDeleteClick = (ids) => {
    // Admins should always be able to delete clients
    if (!isAdmin && !canWrite('clients')) {
      alert(t('clients.permissionDenied'))
      return
    }
    
    setClientsToDelete(ids)
    setShowDeleteModal(true)
  }

  const handleDelete = async () => {
    if (clientsToDelete.length === 0) return
    
    setIsDeleting(true)
    try {
      // Archive related records before deleting clients
      for (const clientId of clientsToDelete) {
        const { error: archiveError } = await supabase.rpc('archive_client_records', {
          p_client_id: clientId,
          p_organization_id: organizationId,
        })
        
        if (archiveError) {
          console.warn('Error archiving records for client:', clientId, archiveError)
          // Continue with deletion even if archiving fails
        }
      }

      // Delete clients
      const { error } = await supabase
        .from('clients')
        .delete()
        .in('id', clientsToDelete)
        .eq('organization_id', organizationId)

      if (error) throw error
      
      setSelected([])
      setShowDeleteModal(false)
      setClientsToDelete([])
      fetchClients()
      success(t('clients.deleteSuccess') || 'Client(s) deleted successfully')
    } catch (error) {
      console.error('Error deleting clients:', error)
      showError(t('clients.deleteError') || 'Failed to delete clients. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCreateClient = async (e) => {
    e.preventDefault()
    
    if (!clientFormData.firstName.trim() || !clientFormData.lastName.trim()) {
      showError(t('clients.nameRequired') || 'First name and last name are required')
      return
    }

    if (!organizationId) {
      showError(t('clients.orgRequired') || 'Organization ID is required')
      return
    }

    setIsCreating(true)
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({
          organization_id: organizationId,
          first_name: clientFormData.firstName.trim(),
          last_name: clientFormData.lastName.trim(),
          email: clientFormData.email.trim() || null,
          phone: clientFormData.phone.trim() || null,
          date_of_birth: clientFormData.dateOfBirth || null,
          gender: clientFormData.gender || null,
        })
        .select()
        .single()

      if (error) throw error

      success(t('clients.createSuccess') || 'Client created successfully')
      setShowClientForm(false)
      setClientFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: '',
      })
      fetchClients()
    } catch (error) {
      console.error('Error creating client:', error)
      showError(t('clients.createError') || 'Failed to create client. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const handleExport = (ids = null) => {
    exportClientsToPDF(clients, ids)
  }

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  const toggleSelectAll = () => {
    if (selected.length === paginatedClients.length) {
      setSelected([])
    } else {
      setSelected(paginatedClients.map((c) => c.id))
    }
  }

  return (
    <div className="section-container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('clients.title')}</h1>
            <p className="text-gray-600">
              {t('clients.subtitle')} • {clients.length} {t('clients.total')}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {clients.length > 0 && (
              <Button variant="secondary" onClick={() => handleExport()} className="flex items-center gap-2">
                <Download size={18} />
                {t('clients.exportAll')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setShowImportExport((v) => !v)}
              className="flex items-center gap-2"
            >
              <Upload size={18} />
              CSV import/export
            </Button>
            <div className="flex gap-2">
              <Button 
                onClick={() => setShowClientForm(true)} 
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <Plus size={20} />
                {t('clients.addClient')}
              </Button>
              <Button 
                variant="secondary"
                onClick={() => navigate('/examination')} 
                className="flex items-center gap-2 whitespace-nowrap"
                title={t('clients.addClientWithExamination') || 'Add client with examination'}
              >
                <FileText size={18} />
                <span className="hidden sm:inline">{t('clients.withExamination') || 'With Examination'}</span>
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Import/Export Panel */}
      {showImportExport && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mb-6"
        >
          <ClientImportExport onImportComplete={() => { fetchClients(); setShowImportExport(false); }} />
        </motion.div>
      )}

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('clients.searchClients')}
        />
      </motion.div>

      {/* Bulk Actions */}
      <BulkActions
        selected={selected}
        onDelete={() => handleDeleteClick(selected)}
        onExport={handleExport}
        onClear={() => setSelected([])}
        resourceName="clients"
      />

      {/* Clients Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SkeletonContainer count={6}>
            <SkeletonCard />
          </SkeletonContainer>
        </div>
      ) : filteredClients.length === 0 ? (
        <Card variant="soft" className="p-12 text-center">
          <Users size={48} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('clients.noClients')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchTerm
              ? t('clients.adjustSearch')
              : t('clients.getStarted')}
          </p>
          {!searchTerm && (
            <div className="flex gap-2 justify-center">
              <Button onClick={() => setShowClientForm(true)} className="flex items-center gap-2 whitespace-nowrap">
                <Plus size={20} />
                {t('clients.addFirstClient')}
              </Button>
              <Button 
                variant="secondary"
                onClick={() => navigate('/examination')} 
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <FileText size={18} />
                {t('clients.withExamination') || 'With Examination'}
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <>
          {paginatedClients.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.length === paginatedClients.length && paginatedClients.length > 0}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600">
                {t('clients.selectAllPage')}
              </span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedClients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card variant="soft" hover className="p-6 relative">
                  {(isAdmin || canWrite('clients')) && (
                    <input
                      type="checkbox"
                      checked={selected.includes(client.id)}
                      onChange={() => toggleSelect(client.id)}
                      className="absolute top-4 right-4 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 z-10"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary-400 to-secondary-400 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                      {getInitials(`${client.first_name} ${client.last_name}`)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {client.first_name} {client.last_name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {client.email || t('clients.noEmail')}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    {client.phone && (
                      <p className="text-gray-600">
                        <span className="font-semibold">{t('clients.phoneLabel')}:</span> {client.phone}
                      </p>
                    )}
                    {client.date_of_birth && (
                      <p className="text-gray-600">
                        <span className="font-semibold">{t('clients.dobLabel')}:</span>{' '}
                        {new Date(client.date_of_birth).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/clients/${client.id}`)}
                      className="flex-1 text-center px-3 py-2 text-primary-600 font-semibold hover:bg-primary-50 rounded-lg transition-colors text-sm flex items-center justify-center gap-1"
                    >
                      <Eye size={16} />
                      {t('clients.view')}
                    </button>
                    {(isAdmin || canWrite('clients')) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteClick([client.id])
                        }}
                        className="px-3 py-2 text-red-600 font-semibold hover:bg-red-50 rounded-lg transition-colors text-sm flex items-center gap-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredClients.length}
          />
        </>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!isDeleting) {
            setShowDeleteModal(false)
            setClientsToDelete([])
          }
        }}
        onConfirm={handleDelete}
        title={t('clients.deleteTitle')}
        message={
          clientsToDelete.length === 1
            ? t('clients.deleteMessage')
            : t('clients.deleteMessageMultiple', { count: clientsToDelete.length })
        }
        itemName="client"
        itemCount={clientsToDelete.length}
        cascadeEffects={[
          t('clients.cascadeEffects.examinations'),
          t('clients.cascadeEffects.carePlans'),
          t('clients.cascadeEffects.therapySessions'),
          t('clients.cascadeEffects.medicalHistory'),
          t('clients.cascadeEffects.documents'),
          t('clients.cascadeEffects.communications'),
        ]}
        requireTyping={clientsToDelete.length > 1}
        confirmText="DELETE"
        isLoading={isDeleting}
        variant="danger"
      />

      {/* Client Creation Modal */}
      <AnimatePresence>
        {showClientForm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => !isCreating && setShowClientForm(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <Card variant="glass" className="w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t('clients.createClient') || 'Create New Client'}
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {t('clients.createClientDescription') || 'Add a new client to your organization'}
                      </p>
                    </div>
                    {!isCreating && (
                      <button
                        onClick={() => setShowClientForm(false)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <X size={20} className="text-gray-500 dark:text-gray-400" />
                      </button>
                    )}
                  </div>

                  <form onSubmit={handleCreateClient} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label={t('clients.firstName') || 'First Name'}
                        type="text"
                        required
                        value={clientFormData.firstName}
                        onChange={(e) => setClientFormData({ ...clientFormData, firstName: e.target.value })}
                        placeholder={t('clients.firstNamePlaceholder') || 'John'}
                        disabled={isCreating}
                      />
                      <Input
                        label={t('clients.lastName') || 'Last Name'}
                        type="text"
                        required
                        value={clientFormData.lastName}
                        onChange={(e) => setClientFormData({ ...clientFormData, lastName: e.target.value })}
                        placeholder={t('clients.lastNamePlaceholder') || 'Doe'}
                        disabled={isCreating}
                      />
                    </div>

                    <Input
                      label={t('clients.email') || 'Email'}
                      type="email"
                      value={clientFormData.email}
                      onChange={(e) => setClientFormData({ ...clientFormData, email: e.target.value })}
                      placeholder={t('clients.emailPlaceholder') || 'john.doe@example.com'}
                      disabled={isCreating}
                    />

                    <Input
                      label={t('clients.phone') || 'Phone'}
                      type="tel"
                      value={clientFormData.phone}
                      onChange={(e) => setClientFormData({ ...clientFormData, phone: e.target.value })}
                      placeholder={t('clients.phonePlaceholder') || '+1234567890'}
                      disabled={isCreating}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label={t('clients.dateOfBirth') || 'Date of Birth'}
                        type="date"
                        value={clientFormData.dateOfBirth}
                        onChange={(e) => setClientFormData({ ...clientFormData, dateOfBirth: e.target.value })}
                        disabled={isCreating}
                      />
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          {t('clients.gender') || 'Gender'}
                        </label>
                        <select
                          value={clientFormData.gender}
                          onChange={(e) => setClientFormData({ ...clientFormData, gender: e.target.value })}
                          className="input-field w-full"
                          disabled={isCreating}
                        >
                          <option value="">{t('clients.selectGender') || 'Select Gender'}</option>
                          <option value="male">{t('clients.genderMale') || 'Male'}</option>
                          <option value="female">{t('clients.genderFemale') || 'Female'}</option>
                          <option value="other">{t('clients.genderOther') || 'Other'}</option>
                          <option value="prefer_not_to_say">{t('clients.genderPreferNotToSay') || 'Prefer not to say'}</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setShowClientForm(false)
                          setClientFormData({
                            firstName: '',
                            lastName: '',
                            email: '',
                            phone: '',
                            dateOfBirth: '',
                            gender: '',
                          })
                        }}
                        disabled={isCreating}
                      >
                        {t('common.buttons.cancel') || 'Cancel'}
                      </Button>
                      <Button type="submit" disabled={isCreating}>
                        {isCreating ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                            />
                            {t('common.buttons.creating') || 'Creating...'}
                          </>
                        ) : (
                          <>
                            <Plus size={18} className="mr-2" />
                            {t('clients.create') || 'Create Client'}
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </Card>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Clients
